(function (Drupal, drupalSettings, once, Cookies) {
  const closeAlert = (alertElement, AlertCookie) => {
    if (!alertElement) return;

    const { id, modifiedDate, level } = alertElement.dataset;

    // Emergency alerts always stay visible as banners — don't save to cookie
    if (level !== 'emergency') {
      AlertCookie.save(id, modifiedDate);
    }

    alertElement.remove();
  };

  const onCloseAlert = (event, AlertCookie) => {
    const { target: element } = event;
    event.preventDefault();
    event.stopPropagation();
    closeAlert(element.closest('.alert'), AlertCookie);
  };

  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const regions = new Map([
    [40, 'Arnprior'],
    [21, 'Brockville'],
    [19, 'Carleton Place, Almonte'],
    [15, 'Kingston'],
    [20, 'Merrickville et Kemptville'],
    [17, 'Marionville'],
    [13, 'Ottawa'],
    [14, 'Comté de Renfrew (écoles à Pembroke)'],
    [16, 'Trenton'],
    // [18, 'Comtés unis de Prescott-Russell'],
  ]);

  const getCTSORegionsMessage = (closedRegions) => {
    const filteredRegions = closedRegions.filter((region) => region !== 18);
    const closedRegionsNames = filteredRegions.map((region) => regions.get(region));
    const message = `<p class="mb-1">En raison des conditions actuelles, le transport scolaire est annulé aujourd'hui pour les régions suivantes :
    ${closedRegionsNames.join(', ')}. Les écoles demeurent ouvertes. <a href="https://transportscolaire.ca/">En savoir plus</a></p>`;
    return message;
  };

  const getCTSOSchoolMessage = () => `<p>En raison des conditions actuelles, le transport scolaire est annulé aujourd'hui pour votre région. L'école demeure ouverte.
    <a href="https://transportscolaire.ca/">En savoir plus</a></p>`;

  const getAlertLinkInfo = (alert, baseURL) => {
    if (alert.attributes.field_url) {
      return {
        alertLabel: alert.attributes.field_url.title,
        alertURL: alert.attributes.field_url.uri,
      };
    }
    if (alert.attributes.body && alert.attributes.body.value && alert.attributes.body.value.length > 0) {
      return {
        alertLabel: alert.attributes.field_page_link_text || Drupal.t('Learn more'),
        alertURL: `${baseURL}${alert.attributes.path.alias}`,
      };
    }
    return { alertLabel: '', alertURL: '' };
  };

  const getPriority = (alert) => {
    if (alert.type === 'ctso') return 2;
    if (alert.level === 'emergency') return 1;
    if (alert.level === 'minor') return 3;
    return 4;
  };

  /**
   * Determines if an alert should be shown as a modal.
   * Only the LATEST (first) emergency alert in the sorted list qualifies.
   * If it has been dismissed and not updated since, no modal is shown — other
   * emergency alerts never "move up" to become the modal.
   * @param {Object} alert - The alert object being evaluated
   * @param {Array} allAlerts - All visible alerts sorted by priority
   * @param {Object} closedAlerts - Dismissed alerts keyed by id with stored modifiedDate
   * @returns {boolean} true if this alert should be shown as a modal
   */
  const shouldShowAsModal = (alert, allAlerts, closedAlerts) => {
    // Only the first emergency in the sorted list can ever be a modal
    const firstEmergency = allAlerts.find((a) => a.level === 'emergency');

    if (!firstEmergency || firstEmergency.id !== alert.id) {
      return false;
    }

    const alertId = String(firstEmergency.id);

    // Never dismissed → show as modal
    if (!(alertId in closedAlerts)) {
      return true;
    }

    // Dismissed but updated since → show as modal again
    const serverDate = new Date(alert.modifiedDate);
    const storedDate = new Date(closedAlerts[alertId]);

    // If either date is invalid, fall back to string comparison
    if (Number.isNaN(serverDate.getTime()) || Number.isNaN(storedDate.getTime())) {
      return alert.modifiedDate !== closedAlerts[alertId];
    }

    return serverDate > storedDate;
  };

  /*
   Alert Modals
  */

  let activeModal = null;
  // let previousFocus = null;
  let handleEscape = null;
  let handleTab = null;

  const focusableSelectors = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // Trap focus within the modal for accessibility.
  const trapFocus = (element) => {
    handleTab = (e) => {
      if (e.key !== 'Tab' || !element) return;

      const focusableElements = element.querySelectorAll(focusableSelectors);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === lastElement) {
        // Tab
        e.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleTab);
  };

  const cleanupModal = () => {
    // Restore body scroll
    document.body.style.overflow = '';

    // Remove event listeners
    if (handleEscape) {
      document.removeEventListener('keydown', handleEscape);
    }
    if (handleTab) {
      document.removeEventListener('keydown', handleTab);
    }

    // Restore focus
    // if (previousFocus && previousFocus.focus) {
    //  previousFocus.focus();
    // }

    activeModal = null;
    // previousFocus = null;
  };

  /*
  Alert Banners
  */

  const createBannerElement = (alert, AlertCookie) => {
    const item = document.createElement('div');
    item.classList.add('alert', `alert--${alert.level}`, `alert--type-${alert.type}`);
    item.setAttribute('data-id', alert.id);
    item.setAttribute('data-modified-date', alert.modifiedDate);
    item.setAttribute('data-level', alert.level);
    item.setAttribute('role', 'alert');
    item.setAttribute('aria-live', 'polite');
    item.setAttribute('aria-atomic', 'true');
    item.innerHTML = `
      <div class="alert__wrapper">
        <span class="alert__icon" aria-hidden="true"></span>
        <div class="alert__content">
          <p class="alert__title">${alert.title}</p>
          <div class="alert__message">
            ${alert.message}
            ${alert.alertURL !== '' ? `<a href="${alert.alertURL}"> ${alert.alertLabel ? alert.alertLabel : Drupal.t('Learn more')} </a>` : ''}
          </div>
        </div>
      </div>
      <button class="alert__close">
        <span class="alert__close__label">${Drupal.t('Close')}<span class="sr-only"> ${Drupal.t('alert banner')} </span></span>
        <span class="icon-close"></span>
      </button>
    `;

    const closeAlertBtn = item.querySelector('.alert__close');
    closeAlertBtn.addEventListener('click', (event) => onCloseAlert(event, AlertCookie));

    return item;
  };

  const convertModalToBanner = (AlertCookie) => {
    const modal = activeModal;

    // Reconstruct the alert object from modal dataset and content
    const alert = {
      id: modal.dataset.id,
      modifiedDate: modal.dataset.modifiedDate,
      level: modal.dataset.level,
      type: modal.dataset.type,
      title: modal.querySelector('.alert-modal__title').textContent,
      message: modal.querySelector('.alert-modal__message').innerHTML,
      alertURL: modal.querySelector('.alert-modal__cta') ? modal.querySelector('.alert-modal__cta').href : '',
      alertLabel: modal.querySelector('.alert-modal__cta') ? modal.querySelector('.alert-modal__cta').textContent.trim() : '',
    };

    const banner = createBannerElement(alert, AlertCookie);
    banner.classList.add('alert--from-modal');

    // Insert banner at the top of existing alerts
    const firstAlert = document.querySelector('.alert');
    if (firstAlert) {
      firstAlert.parentNode.insertBefore(banner, firstAlert);
    } else {
      document.body.prepend(banner);
    }

    // Remove modal
    modal.remove();
  };

  const closeModal = (AlertCookie) => {
    if (!activeModal) return;

    const { id, modifiedDate } = activeModal.dataset;

    // Save to cookie
    AlertCookie.save(id, modifiedDate);

    // Convert modal to banner
    convertModalToBanner(AlertCookie);

    // Clean up
    cleanupModal();
  };

  const setupModalEventListeners = (modal, AlertCookie) => {
    const closeButton = modal.querySelector('.alert-modal__close');
    const overlay = modal.querySelector('.alert-modal__overlay');

    // Close button click
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        closeModal(AlertCookie);
      });
    }

    // Overlay click
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          closeModal(AlertCookie);
        }
      });
    }

    // Escape key
    handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal(AlertCookie);
      }
    };
    document.addEventListener('keydown', handleEscape);
  };

  const initModal = (modalElement, AlertCookie) => {
    activeModal = modalElement;
    // previousFocus = document.activeElement;

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Set up event listeners
    setupModalEventListeners(modalElement, AlertCookie);

    // Set initial focus to close button
    setTimeout(() => {
      const closeButton = modalElement.querySelector('.alert-modal__close');
      if (closeButton) closeButton.focus();
    }, 100);

    // Trap focus within modal
    trapFocus(modalElement);
  };

  const createModalElement = (alert) => {
    const modal = document.createElement('div');
    modal.classList.add('alert-modal');
    modal.setAttribute('data-id', alert.id);
    modal.setAttribute('data-modified-date', alert.modifiedDate);
    modal.setAttribute('data-level', alert.level);
    modal.setAttribute('data-type', alert.type);
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', `modal-title-${alert.id}`);
    modal.setAttribute('aria-describedby', `modal-message-${alert.id}`);

    modal.innerHTML = `
      <div class="alert-modal__overlay"></div>
      <div class="alert-modal__dialog alert-modal__dialog--${alert.level}">
        <div class="alert-modal__header">
          <span class="alert-modal__icon" aria-hidden="true"></span>
          <button class="alert-modal__close" aria-label="${Drupal.t('Close emergency alert')}">
            <span>${Drupal.t('Close')}</span>
            <span class="icon-close" aria-hidden="true"></span>
          </button>
        </div>
        <div class="alert-modal__content">
          <h2 class="alert-modal__title" id="modal-title-${alert.id}">${alert.title}</h2>
          <div class="alert-modal__message" id="modal-message-${alert.id}">
            ${alert.message}
          </div>
          ${alert.alertURL !== '' ? `<a class="alert-modal__cta" href="${alert.alertURL}">${alert.alertLabel ? alert.alertLabel : Drupal.t('Learn More')}</a>` : ''}
        </div>
      </div>
    `;

    return modal;
  };

  const createAlertCookie = () => {
    let AlertCookie = {
      get: () => ({}),
      save: () => {},
    };

    try {
      const COOKIE_KEY = 'cecce-alerts-closed';

      AlertCookie = {
        get () {
          const cookie = Cookies.get(COOKIE_KEY);
          return cookie ? JSON.parse(cookie) : {};
        },

        save (alertID, modifiedDate) {
          const savedAlerts = this.get();
          savedAlerts[alertID] = modifiedDate;
          Cookies.set(COOKIE_KEY, JSON.stringify(savedAlerts), { expires: 7 });
        },
      };
    } catch (e) {
      // Cookies library not available, alert close state will not persist
    }

    return AlertCookie;
  };

  /*
   Alertes listing page
  */

  const renderListeAlertes = (alerts, container) => {
    // Clear loading message
    container.innerHTML = '';

    if (alerts.length === 0) {
      const empty = document.createElement('p');
      empty.textContent = 'Aucune alertes en cours.';
      container.append(empty);
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.classList.add('accordion', 'flex', 'flex-col');
    wrapper.setAttribute('data-multi-expanded', 'false');

    alerts.forEach((alert) => {
      const headingId = `accordion--heading-${alert.id}`;
      const collapseId = `accordion--collapse-${alert.id}`;

      const cta = alert.alertURL
        ? `<p><a href="${alert.alertURL}">${alert.alertLabel ? alert.alertLabel : Drupal.t('Learn more')}</a></p>`
        : '';

      const item = document.createElement('div');
      item.classList.add('cecce-accordion--item', 'border-b', 'border-brand-dark', 'm-4', `cecce-accordion--item--${alert.level}`, `cecce-accordion--item--type-${alert.type}`);
      item.innerHTML = `
        <button type="button" aria-expanded="false"
          class="accordion--item-button pt-1.5 pr-1.5 pl-1.5 pb-3 relative w-full flex items-center justify-between gap-2 md:gap-[7.5rem] rounded-none transition-colors duration-300"
          aria-controls="${collapseId}" id="${headingId}">
          <h3 class="cecce-accordion--item-header text-xl md:text-[1.75rem] text-left text-dark-blue">
            <i class="liste-alertes__icon" aria-hidden="true"></i>${alert.title}
          </h3>
          <i class="icon icon-plus text-brand-dark text-[1.625rem] transition-opacity duration-300"></i>
          <i class="icon icon-minus text-brand-dark text-[1.625rem] transition-opacity duration-300"></i>
        </button>
        <div id="${collapseId}" role="region" aria-labelledby="${headingId}"
          class="accordion--item-panel overflow-hidden h-0 transition-all duration-100">
          <div class="cecce-accordion--item-body text-sm md:text-base pb-3 pr-[2.125rem] md:pr-[9.125rem] pl-1.5 wysiwyg">
            <p>${alert.message}</p>
            ${cta}
          </div>
        </div>
      `;

      const button = item.querySelector('.accordion--item-button');
      const panel = item.querySelector('.accordion--item-panel');

      button.addEventListener('click', () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';

        if (expanded) {
          button.setAttribute('aria-expanded', 'false');
          panel.style.height = '0px';
        } else {
          button.setAttribute('aria-expanded', 'true');
          panel.style.height = `${panel.scrollHeight}px`;
        }
      });

      wrapper.append(item);
    });

    container.append(wrapper);
  };

  /*
   Fetching Alerts
  */

  Drupal.behaviors.cecce_alerts_fetch = {
    attach () {
      once('cecce_alerts_fetch', 'html').forEach(async () => {
        let ctsoAlerts = [];
        let cecceAlerts = null;
        let schoolAlerts = [];

        // Fetch alerts based on site type
        if (drupalSettings.alertUrls.isSchoolBoard === 1) {
          console.log('School board');
          [ctsoAlerts, schoolAlerts] = await Promise.allSettled([
            Drupal.behaviors.cecce_alerts_fetch.fetchCTSO(),
            Drupal.behaviors.cecce_alerts_fetch.fetchCECCE(),
          ]);
        } else {
          console.log('School');
          [ctsoAlerts, schoolAlerts, cecceAlerts] = await Promise.allSettled([
            Drupal.behaviors.cecce_alerts_fetch.fetchCTSO(),
            Drupal.behaviors.cecce_alerts_fetch.fetchSchool(),
            Drupal.behaviors.cecce_alerts_fetch.fetchCECCE(),
          ]);
        }

        const alerts = [];

        // TODO
        // Add some filters on the ctso alerts based on the school region (0 shows all).
        // Add some filters based on the date (we don't use them for now to have some alerts on hand to test).
        // Build up the UI for these alerts.
        // Implement the "hide this alert" feature.
        // Implement the school alerts (for now only the school board can push alerts).
        // Sorts alerts by priorities : Emergency, then CTSO, then the rest.

        if (ctsoAlerts.status === 'fulfilled') {
          ctsoAlerts.value.forEach((alert) => {
            alerts.push({
              id: alert.id,
              title: drupalSettings.alertUrls.isSchoolBoard === 1 ? 'Transport scolaire annulé dans certaines régions' : 'Alerte de transport scolaire',
              message: drupalSettings.alertUrls.isSchoolBoard === 1 ? getCTSORegionsMessage(alert.regions) : getCTSOSchoolMessage(),
              alertURL: '',
              level: 'minor',
              modifiedDate: '',
              type: 'ctso',
            });
          });
        }

        // Process CECCE alerts
        if (cecceAlerts && cecceAlerts.status === 'fulfilled') {
          cecceAlerts.value.forEach((alert) => {
            const { alertLabel, alertURL } = getAlertLinkInfo(alert, 'https://www.ecolecatholique.ca');
            const message = alert.attributes.field_wysiwyg ? alert.attributes.field_wysiwyg.processed : (alert.attributes.body && alert.attributes.body.summary) || '';

            alerts.push({
              id: alert.id,
              title: alert.attributes.title,
              message,
              level: alert.attributes.field_alert_level,
              alertLabel,
              alertURL,
              modifiedDate: alert.attributes.changed,
              type: 'cecce',
            });
          });
        }

        // Process school alerts
        if (schoolAlerts.status === 'fulfilled') {
          schoolAlerts.value.forEach((alert) => {
            const { alertLabel, alertURL } = getAlertLinkInfo(alert, window.location.origin);
            const message = alert.attributes.field_wysiwyg ? alert.attributes.field_wysiwyg.processed : (alert.attributes.body && alert.attributes.body.summary) || '';

            alerts.push({
              id: alert.id,
              title: alert.attributes.title,
              message,
              level: alert.attributes.field_alert_level,
              alertLabel,
              alertURL,
              modifiedDate: alert.attributes.changed,
              type: 'cecce',
            });
          });
        }

        // Sort alerts by priority first, then by modifiedDate descending within same priority
        // This ensures the latest updated emergency is always first
        const sortedAlerts = alerts.sort((a, b) => {
          const priorityDiff = getPriority(a) - getPriority(b);

          if (priorityDiff !== 0) return priorityDiff;

          // Same priority — sort by modifiedDate descending (newest first)
          const dateA = a.modifiedDate ? new Date(a.modifiedDate).getTime() : 0;
          const dateB = b.modifiedDate ? new Date(b.modifiedDate).getTime() : 0;

          return dateB - dateA;
        });

        // Render liste-alertes if present — shows ALL alerts regardless of cookie state
        const listeAlertes = document.getElementById('liste-alertes');
        if (listeAlertes) {
          renderListeAlertes(sortedAlerts, listeAlertes);
        }

        // Get closed alerts from cookie
        const AlertCookie = createAlertCookie();
        const closedAlerts = AlertCookie.get();

        // Filter out closed alerts unless they're modified.
        // Emergency alerts always remain visible — modal vs banner is handled separately.
        const visibleAlerts = sortedAlerts.filter((alert) => {
          const id = String(alert.id);

          // Emergency alerts always show (as modal or banner)
          if (alert.level === 'emergency') return true;

          // not closed → show
          if (!(id in closedAlerts)) return true;

          // If no modifiedDate, don't show again once closed
          if (!alert.modifiedDate || alert.modifiedDate === '') return false;

          const storedModified = closedAlerts[id];

          // show if server is newer
          return new Date(alert.modifiedDate) > new Date(storedModified);
        });

        // Render modal
        const modalAlert = visibleAlerts.find((alert) => shouldShowAsModal(alert, visibleAlerts, closedAlerts));

        if (modalAlert) {
          const modal = createModalElement(modalAlert);

          setTimeout(() => {
            document.body.appendChild(modal);
            initModal(modal, AlertCookie);
          }, 500);
        }

        // Render banners — all visible alerts except the one shown as modal
        const modalAlertId = modalAlert ? modalAlert.id : null;
        const bannerAlerts = visibleAlerts.filter((alert) => alert.id !== modalAlertId);

        if (bannerAlerts.length > 0) {
          const alertArea = new DocumentFragment();

          bannerAlerts.forEach((alert) => {
            const banner = createBannerElement(alert, AlertCookie);
            alertArea.append(banner);
          });

          document.body.prepend(alertArea);
        }
      });
    },

    fetchCTSO: async () => {
      const response = await fetch(`${drupalSettings.alertUrls.externalApi}modified_after%3D${getCurrentDate()}T00:00:00`);

      if (!response.ok) {
        throw new Error(`There was an issue while loading CTSO alerts. ${response.status} : ${response.statusText}`);
      }

      const results = await response.json();
      return results;
    },

    fetchCECCE: async () => {
      const response = await fetch(drupalSettings.alertUrls.schoolBoard);

      if (!response.ok) {
        throw new Error(`There was an issue while loading CECCE alerts. ${response.status} : ${response.statusText}`);
      }

      const results = await response.json();
      return results.data;
    },

    fetchSchool: async () => {
      const response = await fetch('/jsonapi/node/alertes?filter[status]=1');

      if (!response.ok) {
        throw new Error(`There was an issue while loading site alerts. ${response.status} : ${response.statusText}`);
      }

      const results = await response.json();
      return results.data;
    },
  };
}(Drupal, drupalSettings, once, window.Cookies));
